import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './auth';
import { useT, type Key } from './i18n';
import Icon, { ICONS } from './components/Icon';
import Wordmark from './components/Wordmark';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ChangePassword from './pages/ChangePassword';
import Home from './pages/Home';
import Tournaments from './pages/Tournaments';
import TournamentDetail from './pages/TournamentDetail';
import Rankings from './pages/Rankings';
import Players from './pages/Players';
import Admin from './pages/Admin';
import Settings from './pages/Settings';

interface NavItem { to: string; label: Key; short: Key; icon: string; adminOnly?: boolean }
const NAV: NavItem[] = [
  { to: '/', label: 'nav.home', short: 'nav.home', icon: ICONS.home },
  { to: '/tournaments', label: 'nav.tournaments', short: 'nav.tournaments.short', icon: ICONS.trophy },
  { to: '/rankings', label: 'nav.rankings', short: 'nav.rankings.short', icon: ICONS.ranking },
  { to: '/players', label: 'nav.players', short: 'nav.players', icon: ICONS.players },
  { to: '/admin', label: 'nav.admin', short: 'nav.admin', icon: ICONS.admin, adminOnly: true },
  { to: '/settings', label: 'nav.settings', short: 'nav.settings', icon: ICONS.settings },
];

export default function App() {
  const { user, loading } = useAuth();
  const { t } = useT();
  const { pathname } = useLocation();

  if (loading) return <div className="min-h-dvh flex items-center justify-center text-dim">{t('loading')}</div>;
  if (pathname === '/reset') return <ResetPassword />; // reachable signed in or out (link from email)
  if (!user) return pathname === '/forgot' ? <ForgotPassword /> : <Login />;
  if (user.mustChangePassword) return <ChangePassword forced />;

  const nav = NAV.filter((n) => !n.adminOnly || user.role === 'admin');

  return (
    <div className="min-h-dvh flex flex-col lg:flex-row">
      <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:shrink-0 lg:gap-1 lg:p-6 lg:border-r lg:border-ink-800 lg:bg-ink-900/40 lg:sticky lg:top-0 lg:h-dvh lg:overflow-y-auto">
        <div className="mb-8"><Wordmark /></div>
        {nav.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.to === '/'} className="nav-link"><Icon path={n.icon} /> {t(n.label)}</NavLink>
        ))}
      </aside>

      <main className="flex-1 app-safe-area pt-4 lg:pt-8 lg:px-8 lg:pb-10 max-w-screen-xl w-full mx-auto">
        <div className="lg:hidden mb-5"><Wordmark /></div>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/tournaments" element={<Tournaments />} />
          <Route path="/tournaments/:id" element={<TournamentDetail />} />
          <Route path="/rankings" element={<Rankings />} />
          <Route path="/players" element={<Players />} />
          {user.role === 'admin' && <Route path="/admin" element={<Admin />} />}
          <Route path="/settings" element={<Settings />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      <nav className="lg:hidden fixed bottom-0 inset-x-0 bg-ink-900/95 backdrop-blur border-t border-ink-800 bottom-tabs">
        <div className="grid h-16" style={{ gridTemplateColumns: `repeat(${nav.length}, minmax(0, 1fr))` }}>
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === '/'} className="tab-link">
              <Icon path={n.icon} /> {t(n.short)}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
